import API_URL from "../config/api";

class ProxyApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  async request(endpoint, options = {}) {
    const token = localStorage.getItem("authToken");

    const config = {
      ...options,
      headers: {
        ...options.headers,
      },
    };

    if (options.body || ["POST", "PUT", "PATCH"].includes(options.method)) {
      config.headers["Content-Type"] = "application/json";
    }

    if (
      (options.method === "POST" ||
        options.method === "PUT" ||
        options.method === "PATCH") &&
      !options.body
    ) {
      config.body = JSON.stringify({});
    }

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const url = `${this.baseURL}${endpoint}`;

    let response;

    try {
      response = await fetch(url, config);
    } catch (error) {
      throw error;
    }

    if (!response.ok) {
      if (response.status === 401) {
        localStorage.removeItem("authToken");
        window.location.href = "/login";
      }

      const errorData = await response.json().catch(() => ({
        error: `API Error: ${response.statusText}`,
      }));

      const error = new Error(
        errorData.error || `API Error: ${response.statusText}`
      );

      error.errorType = errorData.errorType;
      error.providerAccess = errorData.providerAccess;
      error.status = response.status;

      throw error;
    }

    return response.json();
  }

  async proxyRequest(playlistId, endpoint, options = {}) {
    const profileToken = sessionStorage.getItem(`profile_token_${playlistId}`);

    const config = {
      ...options,
      headers: {
        ...options.headers,
      },
    };

    // Always use POST to send the profile token in body
    config.method = "POST";

    // Parse existing body if it exists and is a string
    let existingBody = {};

    if (options.body) {
      existingBody =
        typeof options.body === "string"
          ? JSON.parse(options.body)
          : options.body;
    }

    // Build the final body with profile token and original method
    config.body = JSON.stringify({
      ...existingBody,
      profileToken,
      originalMethod: options.method || "GET",
    });

    const fullPath = `/v1/dashboard/playlists/proxy/${playlistId}${endpoint}`;

    return this.request(fullPath, config);
  }

  // Profile-related methods
  async getProfiles(playlistId) {
    return this.request(
      `/v1/dashboard/playlists/proxy/${playlistId}/user/profiles`
    );
  }

  async loginProfile(playlistId, profileData) {
    return this.request(
      `/v1/dashboard/playlists/proxy/${playlistId}/login/profile`,
      {
        method: "POST",
        body: JSON.stringify(profileData),
      }
    );
  }

  async createProfile(playlistId, profileData) {
    return this.request(
      `/v1/dashboard/playlists/proxy/${playlistId}/user/profile`,
      {
        method: "POST",
        body: JSON.stringify(profileData),
      }
    );
  }

  async updateProfile(playlistId, profileId, profileData) {
    return this.request(
      `/v1/dashboard/playlists/proxy/${playlistId}/user/profile/${profileId}`,
      {
        method: "PATCH",
        body: JSON.stringify(profileData),
      }
    );
  }

  async deleteProfile(playlistId, profileId) {
    return this.request(
      `/v1/dashboard/playlists/proxy/${playlistId}/user/profile/${profileId}/delete`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );
  }

  async getAvatars(playlistId) {
    return this.request(`/v1/dashboard/playlists/proxy/${playlistId}/avatars`);
  }

  async getSetupInfo(playlistId) {
    return this.proxyRequest(playlistId, "/setup", { method: "POST" });
  }

  async createBookmark(playlistId, mediaId, mediaType) {
    return this.proxyRequest(playlistId, "/bookmarks", {
      method: "POST",
      body: JSON.stringify({ mediaId, mediaType }),
    });
  }

  async deleteBookmark(playlistId, mediaId, mediaType) {
    return this.proxyRequest(playlistId, "/bookmarks/delete", {
      method: "POST",
      body: JSON.stringify({ mediaId, mediaType }),
    });
  }

  // Live TV methods
  async getLiveTVCategories(playlistId) {
    return this.proxyRequest(playlistId, "/live-tv/categories", {
      method: "GET",
    });
  }

  async getUserInfo(playlistId) {
    return this.proxyRequest(playlistId, "/user/info", {
      method: "GET",
    });
  }

  async updateUserSettings(playlistId, settings) {
    return this.proxyRequest(playlistId, "/user/settings", {
      method: "POST",
      body: JSON.stringify(settings),
    });
  }

  async getLiveTVChannels(playlistId, params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.proxyRequest(
      playlistId,
      `/live-tv${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
      }
    );
  }

  async searchChannels(playlistId, term, page = 1, limit = 100) {
    const params = {
      term,
      mediaType: "live-tv",
      page,
      limit,
    };
    const queryString = new URLSearchParams(params).toString();

    return this.proxyRequest(
      playlistId,
      `/search${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
      }
    );
  }

  // Movie methods
  async getMovieCategories(playlistId) {
    return this.proxyRequest(playlistId, "/movies/categories", {
      method: "GET",
    });
  }

  async getMovies(playlistId, params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.proxyRequest(
      playlistId,
      `/movies${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
      }
    );
  }

  async searchMovies(playlistId, term, page = 1, limit = 100) {
    const params = {
      term,
      mediaType: "movie",
      page,
      limit,
    };
    const queryString = new URLSearchParams(params).toString();

    return this.proxyRequest(
      playlistId,
      `/search${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
      }
    );
  }

  // Series methods
  async getSeriesCategories(playlistId) {
    return this.proxyRequest(playlistId, "/series/categories", {
      method: "GET",
    });
  }

  async getSeries(playlistId, params = {}) {
    const queryString = new URLSearchParams(params).toString();

    return this.proxyRequest(
      playlistId,
      `/series${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
      }
    );
  }

  async searchSeries(playlistId, term, page = 1, limit = 100) {
    const params = {
      term,
      mediaType: "tv",
      page,
      limit,
    };
    const queryString = new URLSearchParams(params).toString();

    return this.proxyRequest(
      playlistId,
      `/search${queryString ? `?${queryString}` : ""}`,
      {
        method: "GET",
      }
    );
  }
}

const proxyApiService = new ProxyApiService();

export default proxyApiService;
